import React from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import { Wrench, Shield, CheckCircle } from 'lucide-react';
import FadingPhotoGallery from '../components/FadingPhotoGallery';

const MRO_GALLERY_IMAGES = [
  '/images/67yyq4ah_Gemini_Generated_Image_t6wkxyt6wkxyt6wk.png',
  '/images/3brkw25y_Gemini_Generated_Image_m9rq8om9rq8om9rq.png',
  '/images/2tcqlycd_Gemini_Generated_Image_6dsc136dsc136dsc.png',
  '/images/86wjhxf9_Gemini_Generated_Image_2vest52vest52ves.png',
  '/images/cnprtv0h_WhatsApp_Image_2026-05-28_at_12.50.30_4_.jpeg',
  '/images/ibmm3g55_WhatsApp_Image_2026-05-29_at_10.53.58.jpeg',
  '/images/kg5lwfd2_WhatsApp_Image_2026-05-28_at_14.28.21.jpeg',
  '/images/dwupq1dd_WhatsApp_Image_2026-05-28_at_14.16.09.jpeg',
  '/images/tuijf314_WhatsApp_Image_2026-05-28_at_14.08.52_1_.jpeg',
  '/images/q3nch16q_Picture17.jpg',
  '/images/nt2wt8vn_WhatsApp_Image_2026-05-29_at_14.46.39.jpeg',
  '/images/cvxrz91r_WhatsApp_Image_2026-05-29_at_14.03.24.jpeg',
  '/images/oskfko86_WhatsApp_Image_2026-05-29_at_11.53.55.jpeg',
];

const MRO = () => {
  const systems = [
    "Chassis",
    "Engine",
    "Transmission",
    "Gearbox",
    "Braking System",
    "Suspension",
    "Electricals",
    "Pneumatic Systems",
    "Water & Foam Pumps",
    "Pipelines",
    "Monitors",
    "Tanks",
    "Body Fabrication",
    "Painting"
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-slate-900 text-white py-20 md:py-28 overflow-hidden">
        <div className="absolute inset-0 opacity-[0.08]" style={{
          backgroundImage: 'radial-gradient(circle at 20% 30%, white 1px, transparent 1px), radial-gradient(circle at 80% 70%, white 1px, transparent 1px)',
          backgroundSize: '48px 48px'
        }}></div>
        <div className="absolute left-0 top-0 h-1 w-full bg-red-600"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <span className="eyebrow" style={{ color: '#f4a8b4' }}>MRO Services</span>
            <h1 className="font-display text-4xl md:text-6xl font-extrabold mb-5 mt-3 text-balance">Maintenance, Repair & Overhaul</h1>
            <p className="text-base md:text-lg text-slate-300">
              Restoring fire trucks to peak operational performance with comprehensive overhauling and refurbishing services.
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-6 text-slate-700 leading-relaxed text-base md:text-lg">
            <p>
              At <span className="font-semibold text-red-600">Bhartiya Vehicles and Engineering Co. Ltd.</span>, we believe in extracting the full potential of machinery to its maximum life and saving costs by complete major overhauling and refurbishing fire trucks, restoring them to peak operational performance and extending their service life.
            </p>
            <p>
              Our expertise covers repairing, replacing and supplying spare parts of critical systems including <span className="font-semibold">chassis, engine, transmission, gearbox, braking, suspension, electricals, pneumatic systems, water and foam pumps, pipelines, monitors, tanks, body fabrication, and painting</span> from reputed global OEMs including <span className="font-semibold text-red-600">Caterpillar, Volvo, Tatra, Twindisc, Rosenbauer, Michelin, ZF Wabco, Knorr Bremse</span> to name a few.
            </p>
            <p>
              With advanced engineering capabilities and a strong commitment to quality, we transform aging firefighting vehicles into <span className="font-semibold text-red-600">reliable, mission-ready assets</span>.
            </p>
          </div>
        </div>
      </section>

      {/* Before & After Showcase */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-red-100 text-red-700 hover:bg-red-100 px-4 py-2">Transformation</Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">Before & After</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Witness the remarkable transformation of aging fire trucks into mission-ready assets
            </p>
            <div className="w-24 h-1 bg-red-600 mx-auto mt-6"></div>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Pair 1 - Before */}
            <div className="group">
              <div className="relative overflow-hidden rounded-xl shadow-2xl">
                <img
                  src="/images/sdejvvfn_WhatsApp_Image_2026-05-29_at_11.56.41.jpeg"
                  alt="Fire truck before overhaul"
                  className="w-full h-80 object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute top-4 left-4">
                  <Badge className="bg-slate-900 text-white px-4 py-2 text-sm font-bold uppercase tracking-wider">
                    Before
                  </Badge>
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 to-transparent"></div>
              </div>
              <div className="mt-4 px-2">
                <h3 className="text-xl font-bold text-slate-900 mb-2">Aged & Worn Down</h3>
                <p className="text-slate-600 text-sm">
                  Fire truck arriving at our facility in need of complete overhaul — cab tilted for engine inspection, ready for transformation
                </p>
              </div>
            </div>

            {/* Pair 1 - After */}
            <div className="group">
              <div className="relative overflow-hidden rounded-xl shadow-2xl">
                <img
                  src="/images/ljunsd4n_WhatsApp_Image_2026-05-29_at_11.56.56.jpeg"
                  alt="Fire truck after overhaul"
                  className="w-full h-80 object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute top-4 left-4">
                  <Badge className="bg-red-600 text-white px-4 py-2 text-sm font-bold uppercase tracking-wider">
                    After
                  </Badge>
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-red-900/40 to-transparent"></div>
              </div>
              <div className="mt-4 px-2">
                <h3 className="text-xl font-bold text-slate-900 mb-2">Restored & Mission-Ready</h3>
                <p className="text-slate-600 text-sm">
                  Same vehicle after complete overhaul — fully refurbished, repainted, and restored to peak operational performance
                </p>
              </div>
            </div>

            {/* Pair 2 - Before */}
            <div className="group">
              <div className="relative overflow-hidden rounded-xl shadow-2xl">
                <img
                  src="/images/mptsravm_Gemini_Generated_Image_tnkgzttnkgzttnkg.png"
                  alt="Fire truck chassis before complete restoration"
                  className="w-full h-80 object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute top-4 left-4">
                  <Badge className="bg-slate-900 text-white px-4 py-2 text-sm font-bold uppercase tracking-wider">
                    Before
                  </Badge>
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 to-transparent"></div>
              </div>
              <div className="mt-4 px-2">
                <h3 className="text-xl font-bold text-slate-900 mb-2">Stripped Down to Chassis</h3>
                <p className="text-slate-600 text-sm">
                  ARFFV vehicle disassembled at our workshop — chassis exposed, ready for complete rebuild and refurbishment
                </p>
              </div>
            </div>

            {/* Pair 2 - After */}
            <div className="group">
              <div className="relative overflow-hidden rounded-xl shadow-2xl">
                <img
                  src="/images/2mfikx1s_Gemini_Generated_Image_re08p4re08p4re08.png"
                  alt="Airports Authority of India fire truck after restoration"
                  className="w-full h-80 object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute top-4 left-4">
                  <Badge className="bg-red-600 text-white px-4 py-2 text-sm font-bold uppercase tracking-wider">
                    After
                  </Badge>
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-red-900/40 to-transparent"></div>
              </div>
              <div className="mt-4 px-2">
                <h3 className="text-xl font-bold text-slate-900 mb-2">Fully Restored ARFFV</h3>
                <p className="text-slate-600 text-sm">
                  Airports Authority of India fire truck completely refurbished — ready for deployment with all systems operational
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Workshop Fading Photo Gallery — between Before & After and Critical Systems We Service */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <span className="eyebrow">Inside Our Workshop</span>
            <h2 className="font-display text-3xl md:text-5xl font-extrabold text-slate-900 mt-3 mb-3 text-balance">Restoration in Progress</h2>
            <div className="w-16 h-[3px] bg-red-600 mx-auto"></div>
          </div>
          <div className="max-w-5xl mx-auto">
            <FadingPhotoGallery
              images={MRO_GALLERY_IMAGES}
              displayMs={3000}
              fadeMs={800}
              alt="Bhartiya MRO workshop"
              aspectRatio="16 / 10"
            />
          </div>
        </div>
      </section>

      {/* Systems We Service */}
      <section className="py-24 text-white relative overflow-hidden" style={{ backgroundColor: '#1F1F1F' }}>
        <div className="absolute inset-0 opacity-[0.05]" style={{
          backgroundImage: 'radial-gradient(circle at 20% 30%, white 1px, transparent 1px), radial-gradient(circle at 80% 70%, white 1px, transparent 1px)',
          backgroundSize: '48px 48px'
        }}></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <span className="eyebrow" style={{ color: '#f4a8b4' }}>What We Service</span>
            <h2 className="font-display text-3xl md:text-5xl font-extrabold text-white mt-3 mb-5 text-balance">Critical Systems We Service</h2>
            <div className="w-16 h-[3px] bg-red-600 mx-auto mb-5"></div>
            <p className="text-base md:text-lg text-slate-300 max-w-2xl mx-auto">
              Complete overhauling and refurbishing across all major systems.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {systems.map((system, index) => (
              <Card key={index} className="bg-slate-800/60 border border-slate-700 shadow-none transition-all duration-300">
                <CardContent className="p-5 flex items-center justify-center">
                  <span className="text-sm font-semibold text-red-500 text-center">{system}</span>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Value Proposition */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8">
            <Card className="border-none shadow-lg">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Shield className="h-8 w-8 text-red-600" />
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-3">Maximum Life Extension</h3>
                <p className="text-slate-600">
                  Extract the full potential of your machinery and significantly extend service life through expert overhauling
                </p>
              </CardContent>
            </Card>

            <Card className="border-none shadow-lg">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="h-8 w-8 text-red-600" />
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-3">Peak Performance</h3>
                <p className="text-slate-600">
                  Restore your fire trucks to peak operational performance with our advanced engineering capabilities
                </p>
              </CardContent>
            </Card>

            <Card className="border-none shadow-lg">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Wrench className="h-8 w-8 text-red-600" />
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-3">Mission-Ready Assets</h3>
                <p className="text-slate-600">
                  Transform aging firefighting vehicles into reliable, mission-ready assets with quality guaranteed
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-red-600 to-red-700 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Need Major Overhaul or Maintenance?
          </h2>
          <p className="text-lg mb-8 max-w-2xl mx-auto text-red-100">
            Get in touch with our expert MRO team to restore your fleet to peak performance
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-white text-red-600 hover:bg-slate-100 px-8 py-6 text-lg">
              Contact Our MRO Team
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default MRO;
